import React from 'react'
import { Collapse, Select, Typography } from 'antd'
import PropTypes from 'prop-types'

import { ConcessionGalleryItemsEditor, ImageUploader } from '~components'
import { Input } from '~stories'

import { CONCESSION_GALLERY_VARIANTS, reconcileGalleryItemsForVariant } from '../../utils/concessionGalleryItemDefaults'
import {
  VENUE_RENTALS_HERO_DISPLAY_OPTIONS,
  VENUE_RENTALS_HERO_DISPLAY_TYPES
} from '../../utils/venueRentalsPageDefaults'
import styles from '../FAQPageSections/FAQAdminFields.module.css'
import heroStyles from './VenueRentalsHeroFields.module.css'

const { Panel } = Collapse
const { Text } = Typography

const VenueRentalsHeroFields = ({
  hero = {},
  heroErrors = {},
  onChange,
  showValidationErrors = false
}) => {
  const displayType = hero?.display_type || VENUE_RENTALS_HERO_DISPLAY_TYPES.STATIC_IMAGE
  const isCarousel = displayType === VENUE_RENTALS_HERO_DISPLAY_TYPES.CAROUSEL
  const carouselErrors = showValidationErrors ? (heroErrors?.carousel || {}) : { title: '', cardCount: '', items: [] }

  const updateHero = partial => onChange(partial)

  return (
    <Collapse
      accordion
      defaultActiveKey={['hero']}
      expandIconPosition='right'
      style={{ marginTop: 16 }}
    >
      <Panel
        key='hero'
        header='Hero'
        style={{ fontSize: 20 }}
      >
        <div className={styles.fieldGroup}>
          <Text className={styles.hint} style={{ display: 'block', marginBottom: 16 }}>
            Configure the hero for venue rental landing and space pages. Choose a static image
            with contact details, or a carousel with up to 5 image or YouTube video cards.
          </Text>

          <div className={styles.fieldRow}>
            <Text className={styles.fieldLabel}>Hero Type*</Text>
            <Select
              onChange={value => {
                if (value === VENUE_RENTALS_HERO_DISPLAY_TYPES.CAROUSEL) {
                  updateHero({
                    display_type: value,
                    gallery_items: reconcileGalleryItemsForVariant(
                      hero?.gallery_items,
                      CONCESSION_GALLERY_VARIANTS.MEDIA_ONLY_CAROUSEL
                    )
                  })
                  return
                }
                updateHero({ display_type: value })
              }}
              options={VENUE_RENTALS_HERO_DISPLAY_OPTIONS}
              style={{ width: 320 }}
              value={displayType}
            />
          </div>

          <div className={styles.fieldRow}>
            <Text className={`${styles.fieldLabel}${showValidationErrors && heroErrors?.header ? ` ${styles.fieldLabelError}` : ''}`}>
              Hero Header*
            </Text>
            <Input
              onChange={e => updateHero({ header: e.target.value })}
              placeholder={isCarousel ? 'Space name' : 'Venue Rentals'}
              status={showValidationErrors && heroErrors?.header ? 'error' : undefined}
              value={hero?.header || ''}
            />
            {showValidationErrors && heroErrors?.header && (
              <Text className={styles.fieldError}>{heroErrors.header}</Text>
            )}
          </div>

          {isCarousel ? (
            <>
              {carouselErrors.cardCount && (
                <Text className={styles.fieldError}>{carouselErrors.cardCount}</Text>
              )}
              <ConcessionGalleryItemsEditor
                galleryItems={hero?.gallery_items || []}
                itemErrors={carouselErrors.items || []}
                onChange={galleryItems => updateHero({ gallery_items: galleryItems })}
                variantType={CONCESSION_GALLERY_VARIANTS.MEDIA_ONLY_CAROUSEL}
              />
            </>
          ) : (
            <>
              <div className={styles.fieldRow}>
                <Text className={styles.fieldLabel}>Hero Image*</Text>
                <ImageUploader
                  handleUpload={url => updateHero({
                    image: {
                      ...(hero?.image || {}),
                      url
                    }
                  })}
                  logoUrl={hero?.image?.url}
                  pageType="venue-rentals"
                />
                {showValidationErrors && heroErrors?.image && (
                  <Text className={styles.fieldError}>{heroErrors.image}</Text>
                )}
              </div>

              <div className={styles.fieldRow}>
                <Text className={styles.fieldLabel}>Image Alt Text</Text>
                <Input
                  onChange={e => updateHero({
                    image: {
                      ...(hero?.image || {}),
                      alt: e.target.value
                    }
                  })}
                  placeholder="Describe the hero image"
                  value={hero?.image?.alt || ''}
                />
              </div>

              <div className={heroStyles.fieldRowTwoCol}>
                <div className={heroStyles.fieldCol}>
                  <Text className={styles.fieldLabel}>CTA Label</Text>
                  <Input
                    onChange={e => updateHero({
                      cta: {
                        ...(hero?.cta || {}),
                        label: e.target.value
                      }
                    })}
                    placeholder="Contact Us"
                    value={hero?.cta?.label || ''}
                  />
                </div>

                <div className={heroStyles.fieldCol}>
                  <Text className={styles.fieldLabel}>CTA URL</Text>
                  <Input
                    onChange={e => updateHero({
                      cta: {
                        ...(hero?.cta || {}),
                        url: e.target.value
                      }
                    })}
                    placeholder="Formstack form ID"
                    value={hero?.cta?.url || ''}
                  />
                  <Text className={styles.hint}>
                    Formstack form ID used to open the contact form as a popup modal on the frontend.
                  </Text>
                </div>
              </div>

              <div className={styles.fieldRow}>
                <Text className={styles.fieldLabel}>Email Icon</Text>
                <ImageUploader
                  handleUpload={iconUrl => updateHero({
                    email: {
                      ...(hero?.email || {}),
                      icon_url: iconUrl
                    }
                  })}
                  logoUrl={hero?.email?.icon_url}
                  pageType="venue-rentals"
                />
              </div>

              <div className={heroStyles.fieldRowTwoCol}>
                <div className={heroStyles.fieldCol}>
                  <Text className={styles.fieldLabel}>Email Text</Text>
                  <Input
                    onChange={e => updateHero({
                      email: {
                        ...(hero?.email || {}),
                        text: e.target.value
                      }
                    })}
                    placeholder="specialevents@msg.com"
                    value={hero?.email?.text || ''}
                  />
                </div>

                <div className={heroStyles.fieldCol}>
                  <Text className={styles.fieldLabel}>Email-to Link</Text>
                  <Input
                    onChange={e => updateHero({
                      email: {
                        ...(hero?.email || {}),
                        href: e.target.value
                      }
                    })}
                    placeholder="mailto:specialevents@msg.com"
                    value={hero?.email?.href || ''}
                  />
                  <Text className={styles.hint}>Use a mailto link (e.g. mailto:specialevents@msg.com).</Text>
                </div>
              </div>
            </>
          )}
        </div>
      </Panel>
    </Collapse>
  )
}

VenueRentalsHeroFields.propTypes = {
  hero: PropTypes.object,
  heroErrors: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  showValidationErrors: PropTypes.bool
}

export default VenueRentalsHeroFields
